import {
  Component,
  DoCheck,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { LogoComponent } from '../../atoms/logo/logo.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { DropdownNavComponent } from './dropdown-nav/dropdown-nav.component';
import { ButtonComponent } from '../button/button.component';
import { FlyoutComponent } from './flyout/flyout.component';
import MenuItem, {
  FlyoutData,
} from '../../../shared/interfaces/menuItems.interface';
import { Sizes } from '../../../shared/enums/shared.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';

import { NavigationPage } from '../../../shared/interfaces/navigation-page.interface';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { FlyoutService } from '../../../services/flyout.service';
import { GlobalSearchType } from '../../../shared/enums/global-search-type.enums';
import { MenuService } from '../../../services/menu.service';
import { HrefType } from '../../../shared/enums/href-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { LinkComponent } from '../../atoms/link/link.component';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { TopBarLoaderComponent } from './top-bar-loader/top-bar-loader.component';

@Component({
  selector: 'hdi-top-bar',
  standalone: true,
  imports: [
    LogoComponent,
    CommonModule,
    RouterModule,
    IconComponent,
    DropdownNavComponent,
    ButtonComponent,
    FlyoutComponent,
    LinkComponent,
    TopBarLoaderComponent,
  ],
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
})
export class TopBarComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
  @Input() mainMenu: Array<MenuItem> = [];
  @Input() metaMenu: Array<MenuItem> = [];
  @Input() flyoutData: FlyoutData[] = [
    {
      title: '',
      url: '',
      subpages: [],
    },
  ];

  public readonly Sizes = Sizes;
  public readonly ButtonType = ButtonType;

  public readonly GlobalSearchType = GlobalSearchType;
  readonly HrefType = HrefType;
  readonly LinkType = LinkType;

  public routeSubscription$: Subscription = new Subscription();
  public isMenuOpen: boolean = false;
  public isFlyoutOpen: boolean = false;
  public slideUp: boolean = false;
  public currentUrl: string = '';
  public globalSearchOpen: boolean = false;
  public currentFlyout: Array<NavigationPage> = [];
  public flyoutOverview: { title: string; url: string; isSimple: boolean } = {
    title: '',
    url: '',
    isSimple: true,
  };
  public dropDownMenu: Array<MenuItem> = [];
  public isLoading = true;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly flyoutService: FlyoutService,
    private readonly renderer: Renderer2,
    private readonly menuService: MenuService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleMenu();
    }
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
      });
    this.menuService.setMainMenu(this.mainMenu);
    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
    }
  }

  ngOnChanges(): void {
    this.dropDownMenu = [...this.mainMenu, ...this.metaMenu];
  }

  ngDoCheck(): void {
    const inpageNavigation = this.document.querySelector(
      '.inpage-navigation-sticky'
    );
    this.slideUp = !!(
      inpageNavigation &&
      inpageNavigation.getBoundingClientRect().top <= 60 &&
      inpageNavigation.getBoundingClientRect().bottom >= 0
    );
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    this.toggleScrollLock(this.isMenuOpen);
    const burger = this.document.querySelector('.burger-menu');
    if (burger) {
      this.renderer.addClass(burger, 'open');
      if (this.isMenuOpen) {
        this.renderer.addClass(burger, 'open');
      } else {
        this.renderer.removeClass(burger, 'open');
      }
    }
  }

  toggleScrollLock(isBlocked: boolean): void {
    const body = this.document.querySelector('body');
    if (body) {
      if (isBlocked) {
        this.renderer.setStyle(body, 'overflow', 'hidden');
      } else {
        this.renderer.removeStyle(body, 'overflow');
      }
    }
  }

  openFlyout(item: MenuItem): void {
    this.isFlyoutOpen = false;
    setTimeout(() => {
      this.isFlyoutOpen = true;
      const currentFlyoutData = this.flyoutData.find(
        (data) => data.title === item.title
      );
      if (currentFlyoutData) {
        let title = '';
        let url = '';
        safeAssign(
          () => {
            this.currentFlyout = currentFlyoutData.subpages;
          },
          'Assigning currentFlyout',
          this.errorHandler
        );
        safeAssign(
          () => {
            title = currentFlyoutData.title;
          },
          'Assigning title',
          this.errorHandler
        );
        safeAssign(
          () => {
            url = currentFlyoutData.url;
          },
          'Assigning url',
          this.errorHandler
        );
        this.flyoutOverview = {
          title: title,
          url: url,
          isSimple: true,
        };
        this.flyoutService.openFlyout();
      } else {
        this.closeFlyout();
        this.flyoutOverview = { title: '', url: '', isSimple: true };
        this.router.navigate([item.url]);
      }
    }, 0);
  }

  closeFlyout(): void {
    this.isFlyoutOpen = false;
  }

  navigateToHomepage(): void {
    this.isFlyoutOpen = false;
    this.router.navigate(['/']);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  toggleGlobalSearch(): void {
    this.globalSearchOpen = !this.globalSearchOpen;
    if (this.globalSearchOpen) {
      onscroll = () => {
        this.globalSearchOpen = false;
      };
    }
  }

  handleCloseMenu(): void {
    this.isMenuOpen = false;
    const burger = this.document.querySelector('.burger-menu');
    this.renderer.removeClass(burger, 'open');
    const body = this.document.querySelector('body');
    this.renderer.removeStyle(body, 'overflow');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
